import {
  getTriviaContinue,
  getTriviaInfo,
  getTriviaQuestion,
  submitAnswer,
} from '@wwe-portal/web/services/client/trivia-rumble';
import { TriviaRumbleQuestion } from '@wwe-portal/entities/trivia-rumble.type';
import { createStore, useStore } from 'zustand';
import { wait } from '../../../../../packages/ui/lib/timer';

export type GameStatus =
  | 'idle'
  | 'ready'
  | 'playing'
  | 'waiting'
  | 'won'
  | 'lost';

export interface TriviaGameState {
  gameId: number | undefined;
  gameConfig:
    | {
        energyCost: number;
        numberOfQuestions: number;
        questionTime: number;
        retryCountDown: number;
      }
    | undefined;
  gameStartAtMs: number | undefined;
  gameStatus: GameStatus;
  gamePoint: number | undefined;
  gameReward: number | undefined;
  gameData:
    | {
        questionIndex: number;
        currentQuestion: TriviaRumbleQuestion;
        currentAnswer: string | undefined;
        correctAnswer: string | undefined;
        previousPoint: number;
        currentPoint: number;
        retryCost: number | undefined;
        retryCountdown: number | undefined;
        retryCountdownTimer: NodeJS.Timeout | undefined;
      }
    | undefined;
  questionCountdown:
    | {
        msLeft: number;
        timer: number | undefined;
        stopped: boolean;
      }
    | undefined;
}

export interface TriviaGameAction {
  startCountdown: () => void;
  stopCountdown: () => void;

  startGame: () => Promise<void>;
  prepareNextQuestion: () => Promise<void>;
  startQuestion: () => void;
  winGame: () => void;
  loseGame: () => void;
  closeGame: () => void;
  playAgain: () => Promise<void>;
  countdownRetry: () => void;
  clearRetryCountdown: () => void;
  continueAnswer: () => Promise<void>;

  choseAnswer: (payload: { optionId: string }) => Promise<void>;
}

type TriviaGameValue = TriviaGameState & TriviaGameAction;

const defaultState: TriviaGameState = {
  gameId: undefined,
  gameConfig: undefined,
  gameStartAtMs: undefined,
  gameStatus: 'idle',
  gamePoint: undefined,
  gameReward: undefined,
  gameData: undefined,
  questionCountdown: undefined,
};

const SHOW_ANSWER_DELAY_MS = 3000;
const NEXT_QUESTION_DELAY_MS = 3000;
const START_GAME_DELAY_MS = 4000;

export const triviaStore = createStore<TriviaGameValue>((set, get) => ({
  ...defaultState,
  startGame: async () => {
    const response = await getTriviaInfo();
    const firstQuestionResponse = await getTriviaQuestion(
      response.data.data.gameId
    );
    const gameStartAtMs = Date.now();

    // firstQuestionResponse.data.data
    const { gameId, config } = response.data.data;
    set({
      gameId,
      gameConfig: config,
      gameStatus: 'ready',
      gameStartAtMs,
      gameData: {
        questionIndex: 0,
        currentQuestion: firstQuestionResponse.data.data,
        currentAnswer: undefined,
        correctAnswer: undefined,
        previousPoint: 0,
        currentPoint: 0,
        retryCost: undefined,
        retryCountdown: undefined,
        retryCountdownTimer: undefined,
      },
    });

    // Start game after 4 seconds
    setTimeout(() => {
      const gameData = get().gameData;
      const gameStatus = get().gameStatus;
      if (!gameData || gameStatus !== 'ready') {
        get().closeGame();
        return;
      }
      get().startQuestion();
    }, START_GAME_DELAY_MS);
  },
  prepareNextQuestion: async () => {
    const doPrepare = async () => {
      const gameId = get().gameId;
      const gameData = get().gameData;
      if (!gameId || !gameData) return;
      set({ gameStatus: 'waiting' });
      const nextQuestionResponse = await getTriviaQuestion(gameId);
      set({
        gameData: {
          ...gameData,
          questionIndex: gameData.questionIndex + 1,
          currentQuestion: nextQuestionResponse.data.data,
          currentAnswer: undefined,
          correctAnswer: undefined,
        },
      });
    };
    await Promise.all([doPrepare(), wait(NEXT_QUESTION_DELAY_MS)]);
    get().startQuestion();
  },
  startQuestion: () => {
    set({
      gameStatus: 'playing',
    });
    get().startCountdown();
  },
  startCountdown: () => {
    const gameConfig = get().gameConfig;
    if (!gameConfig) return;

    let start = performance.now();
    const handleCallback = (time: number) => {
      const questionCountdown = get().questionCountdown;
      if (!questionCountdown) {
        cancelAnimationFrame(timer);
        return;
      }
      const duration = time - start;
      start = time;
      const nextMsLeft = Math.max(0, questionCountdown.msLeft - duration);

      // If countdown is over, end game
      if (nextMsLeft <= 0) {
        cancelAnimationFrame(timer);
        set({
          questionCountdown: {
            ...questionCountdown,
            msLeft: 0,
            stopped: true,
            timer: undefined,
          },
        });
        get().choseAnswer({ optionId: '-1' });
        return;
      }

      const stopped = questionCountdown.stopped || nextMsLeft <= 0;

      // If countdown is not over, update countdown
      set({
        questionCountdown: {
          ...questionCountdown,
          msLeft: stopped ? questionCountdown.msLeft : nextMsLeft,
          stopped,
        },
      });

      if (!stopped) requestAnimationFrame(handleCallback);
    };
    const timer = requestAnimationFrame(handleCallback);
    set({
      questionCountdown: {
        msLeft: gameConfig.questionTime * 1000,
        timer,
        stopped: false,
      },
    });
  },
  stopCountdown: () => {
    const questionCountdown = get().questionCountdown;
    if (!questionCountdown) return;
    if (questionCountdown?.timer) cancelAnimationFrame(questionCountdown.timer);
    set({
      questionCountdown: {
        ...questionCountdown,
        stopped: true,
      },
    });
  },
  winGame: () => {
    set({ gameStatus: 'won' });
  },
  loseGame: async () => {
    const gameConfig = get().gameConfig;
    const gameData = get().gameData;
    if (!gameConfig || !gameData) return;
    set({
      gameStatus: 'lost',
    });
    const questionIndex = gameData.questionIndex;
    const totalQuestions = gameConfig.numberOfQuestions;
    const isLastQuestion = questionIndex >= totalQuestions - 1;
    if (!isLastQuestion) {
      get().countdownRetry();
    }
  },
  countdownRetry: () => {
    const gameData = get().gameData;
    const gameConfig = get().gameConfig;
    if (!gameData || !gameConfig) return;
    const timer = setInterval(() => {
      const gameData = get().gameData;
      if (
        !gameData ||
        gameData.retryCountdown == null ||
        gameData.retryCountdown - 1 < 0
      ) {
        clearInterval(timer);
        get().clearRetryCountdown();
        get().closeGame();
        return;
      }

      const nextRetryCountdown = gameData.retryCountdown - 1;

      set({
        gameData: {
          ...gameData,
          retryCountdown: nextRetryCountdown,
        },
      });
    }, 1000);
    set({
      gameData: {
        ...gameData,
        retryCountdown: gameConfig.retryCountDown,
        retryCountdownTimer: timer,
      },
    });
  },
  clearRetryCountdown: () => {
    const gameData = get().gameData;
    if (!gameData) return;
    clearInterval(gameData.retryCountdownTimer);
    set({
      gameData: {
        ...gameData,
        retryCountdownTimer: undefined,
      },
    });
  },
  continueAnswer: async () => {
    const gameData = get().gameData;
    const gameConfig = get().gameConfig;
    const gameId = get().gameId;
    if (!gameData || !gameConfig || !gameId) return;
    const neededWebCoin = gameData.retryCost;
    get().clearRetryCountdown();
    await getTriviaContinue({ webCoin: neededWebCoin, gameId });
    set({
      gameStatus: 'playing',
      gameData: {
        ...gameData,
        retryCountdown: undefined,
      },
    });
    await get().prepareNextQuestion();
  },
  closeGame: () => {
    set({
      ...defaultState,
    });
  },
  playAgain: async () => {
    get().closeGame();
    await get().startGame();
  },
  choseAnswer: async ({ optionId }) => {
    const currentGameData = get().gameData;
    const gameId = get().gameId;
    const gameConfig = get().gameConfig;
    const questionCountdown = get().questionCountdown;
    if (!currentGameData || !gameId || !questionCountdown || !gameConfig) {
      return;
    }

    const totalQuestions = gameConfig.numberOfQuestions;
    const questionIndex = currentGameData.questionIndex;

    const nextGameData = {
      ...currentGameData,
      currentAnswer: optionId,
    };
    get().stopCountdown();

    set({
      gameData: nextGameData,
    });
    const duration = questionCountdown.msLeft / 1000;
    const response = await submitAnswer({
      duration,
      gameId,
      optionId,
      questionId: currentGameData.currentQuestion.id,
      resultId: currentGameData.currentQuestion.resultId,
    });

    const point = response.data.data.point;
    const rightAnswer = response.data.data.rightAnswer;
    set({
      gameData: {
        ...nextGameData,
        currentPoint: nextGameData.currentPoint + point,
        correctAnswer: rightAnswer,
        retryCost: response.data.data.webCoin,
      },
    });

    await wait(SHOW_ANSWER_DELAY_MS);
    if (questionIndex >= totalQuestions - 1) {
      if (rightAnswer === optionId) {
        get().winGame();
      } else {
        get().loseGame();
      }
      return;
    }

    if (rightAnswer === optionId) {
      await get().prepareNextQuestion();
      return;
    }

    get().loseGame();
  },
}));

export const useTriviaStore = <T>(selector: (state: TriviaGameValue) => T) => {
  return useStore(triviaStore, selector);
};

export const useGameStatus = () => {
  return useTriviaStore((state) => state.gameStatus);
};
